"use client";

import { useState, type ReactNode } from "react";
import { Maximize2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@stockline/ui/components/dialog";

interface ExpandableChartProps {
  readonly title?: string;
  readonly renderChart: (opts: {
    height: number;
    showTitle: boolean;
  }) => ReactNode;
}

const COMPACT_HEIGHT = 300;
const EXPANDED_HEIGHT = 600;

export function ExpandableChart({ title, renderChart }: ExpandableChartProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const expandLabel = t("ai.chart.expand", "Expand chart");

  return (
    <>
      <div className="relative group">
        {renderChart({ height: COMPACT_HEIGHT, showTitle: true })}
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={expandLabel}
          title={expandLabel}
          className="absolute top-0 right-0 p-1 rounded-md text-muted-foreground opacity-50 hover:opacity-100 hover:bg-muted hover:text-foreground transition-opacity focus:outline-none focus:opacity-100 focus:ring-2 focus:ring-ring"
        >
          <Maximize2 className="h-3.5 w-3.5" />
        </button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-[min(1200px,90vw)] w-[90vw] z-[200]"
          overlayClassName="z-[200]"
          aria-describedby={undefined}
        >
          <DialogHeader>
            <DialogTitle>{title ?? expandLabel}</DialogTitle>
          </DialogHeader>
          <div className="pt-2">
            {renderChart({ height: EXPANDED_HEIGHT, showTitle: false })}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
